import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Snackbar,
  Typography,
  Modal,
  ModalDialog,
} from '@mui/joy';
import { useLocation } from 'react-router-dom';
import { usePricingStore } from '../hooks/usePricingStore';
import { directSupabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';
import SupplierManagement from './SupplierManagement';
import { PriceManagement } from './PriceManagement';
import SupplierSelector from './SupplierSelector';
import UnifiedEmailTemplateEditor from './UnifiedEmailTemplateEditor';
import EmailActivityDashboard from './EmailActivityDashboard';
import AllEventsManager from './AllEventsManager';
import NotificationsDashboard from './NotificationsDashboard';
import UserManagement from './UserManagement';
import PdfExportConfigPanel from '../modules/docs/components/PdfExportConfigPanel';
import { FreeAgentAuth } from './FreeAgentAuth';
import ChangeLogPanel from './ChangeLogPanel';
import ConsumerContractRegistryPanel from '../modules/docs/components/ConsumerContractRegistryPanel';
import UiThemeAdminPanel from './UiThemeAdminPanel';
import VdScenarioEnginePanel from '../modules/docs/testing/VdScenarioEnginePanel';
import SurveyIntegrityPanel from './SurveyIntegrityPanel';
import OrganisationsPanel from './OrganisationsPanel';

type Supplier = Database['public']['Tables']['suppliers']['Row'];

// Simple error boundary for dashboard
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any}> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ color: 'red' }}>Error: {String(this.state.error)}</div>;
    }
    return this.props.children;
  }
}

export const AdminPanel = () => {
  const location = useLocation();
  const { pricing, restoreDefaultPricing } = usePricingStore();
  const [localPricing, setLocalPricing] = useState<typeof pricing | null>(null);
  const [snack, setSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('Pricing saved.');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // Get current tab from URL query parameter
  const getCurrentTab = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('tab') || 'pricing';
  };

  const currentTab = getCurrentTab();

  const tabLabel = (tab: string) => {
    if (tab === 'email-activity') return 'activity';
    if (tab === 'consumer-contracts') return 'Consumer Contract Registry';
    if (tab === 'vd-scenarios') return 'Scenario engine';
    if (tab === 'survey-integrity') return 'Survey Integrity';
    return tab.replace('-', ' ');
  };

  const loadSuppliers = useCallback(async () => {
    try {
      const { data, error } = await directSupabase
        .from('suppliers')
        .select('*')
        .order('supplier_name', { ascending: true });
      
      if (error) {
        throw new Error(error.message);
      }
      
      const suppliers = data || [];
      setSuppliers(suppliers);
      
      // Auto-select the first glass supplier if none is selected and we have suppliers
      if (!selectedSupplier && suppliers.length > 0) {
        const firstGlassSupplier = suppliers.find((s: any) => s.category === 'Glass');
        if (firstGlassSupplier) {
          console.log('Auto-selecting first glass supplier:', firstGlassSupplier.supplier_name);
          setSelectedSupplier(firstGlassSupplier.id);
        }
      }
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    }
  }, [selectedSupplier]);

  useEffect(() => {
    setLocalPricing(pricing ? JSON.parse(JSON.stringify(pricing)) : null);
    loadSuppliers();
  }, [pricing, loadSuppliers]);

  const handleRestore = () => {
    if (localPricing) {
      restoreDefaultPricing();
      setSnackMessage('Pricing restored to defaults.');
      setSnack(true);
    }
    setShowRestoreConfirm(false);
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'pricing':
        return (
          <Box>
            {/* Supplier Selector for Pricing Tab */}
            {currentTab === 'pricing' && (
              <Box sx={{ 
                mb: 2,
                width: '100%',
                overflowX: 'auto',
                '& > *': { minWidth: { xs: '280px', sm: 'auto' } }
              }}>
                <SupplierSelector
                  value={selectedSupplier}
                  onChange={setSelectedSupplier}
                  supplierType="Glass"
                />
              </Box>
            )}
            <PriceManagement
              suppliers={suppliers}
              selectedSupplier={selectedSupplier}
              onSupplierChange={setSelectedSupplier}
            />
          </Box>
        );
      case 'suppliers':
        return <SupplierManagement />;
      case 'email-templates':
        return <UnifiedEmailTemplateEditor />;
      case 'email-activity':
        return (
          <ErrorBoundary>
            <EmailActivityDashboard />
          </ErrorBoundary>
        );
      case 'change-log':
        return (
          <ErrorBoundary>
            <ChangeLogPanel />
          </ErrorBoundary>
        );
      case 'event-manager':
        return (
          <ErrorBoundary>
            <AllEventsManager />
          </ErrorBoundary>
        );
      case 'notifications':
        return (
          <ErrorBoundary>
            <NotificationsDashboard />
          </ErrorBoundary>
        );
      case 'users':
        return <UserManagement />;
      case 'organisations':
        return (
          <ErrorBoundary>
            <OrganisationsPanel />
          </ErrorBoundary>
        );
      case 'pdf-export':
        return <PdfExportConfigPanel />;
      case 'appearance':
        return (
          <ErrorBoundary>
            <UiThemeAdminPanel />
          </ErrorBoundary>
        );
      case 'consumer-contracts':
        return (
          <ErrorBoundary>
            <ConsumerContractRegistryPanel />
          </ErrorBoundary>
        );
      case 'survey-integrity':
        return (
          <ErrorBoundary>
            <SurveyIntegrityPanel />
          </ErrorBoundary>
        );
      case 'freeagent':
        return (
          <ErrorBoundary>
            <FreeAgentAuth />
          </ErrorBoundary>
        );
      case 'vd-scenarios':
        return (
          <ErrorBoundary>
            <VdScenarioEnginePanel />
          </ErrorBoundary>
        );
      default:
        return (
          <Box>
            <Typography level="h4" sx={{ mb: 2 }}>
              Welcome to Admin Panel
            </Typography>
            <Typography level="body-lg" sx={{ color: 'neutral.500' }}>
              Manage your system settings and configurations.
            </Typography>
          </Box>
        );
    }
  };

  if (!localPricing && currentTab === 'pricing') {
    return <Typography>Loading pricing data…</Typography>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          level="h1" 
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' },
            wordBreak: 'break-word'
          }}
        >
          Admin Panel - {tabLabel(currentTab).charAt(0).toUpperCase() + tabLabel(currentTab).slice(1)}
        </Typography>
        <Typography level="body-lg" sx={{ color: 'neutral.500' }}>
          Manage your system settings and configurations.
        </Typography>
      </Box>

      {/* Content */}
      {renderContent()}

      {/* Restore Confirmation Modal */}
      <Modal open={showRestoreConfirm} onClose={() => setShowRestoreConfirm(false)}>
        <ModalDialog>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Restore Default Pricing?
          </Typography>
          <Typography sx={{ mb: 3 }}>
            This will reset all pricing to the default values. This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setShowRestoreConfirm(false)}>
              Cancel
            </Button>
            <Button variant="solid" color="danger" onClick={handleRestore}>
              Restore
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snack}
        onClose={() => setSnack(false)}
        autoHideDuration={3000}
        color="success"
      >
        {snackMessage}
      </Snackbar>
    </Box>
  );
};

export default AdminPanel;
